import styled, { css } from 'styled-components'

import { StylesMap } from 'shared/styles/types'
import Flex from 'shared/ui/Flex'

enum States {
  error = 'error',
  success = 'success',
  default = 'default',
  disabled = 'disabled'
}

type Size = 'default' | 'small'

const STATES_CONTAINER_STYLES_MAP: StylesMap<States> = {
  [States.default]: css<ContainerProps>`
    &:hover {
      border-color: ${({ theme }) => theme.color.base.c5};
    }
    &:focus-within {
      border: 1px solid ${({ theme }) => theme.color.brand_02};
    }
  `,
  [States.disabled]: css`
    border: 1px solid ${({ theme }) => theme.color.base.c3};
  `,
  [States.error]: css<ContainerProps>`
    border: 1px solid ${({ theme }) => theme.color.error};
  `,
  [States.success]: css`
    border: 1px solid ${({ theme }) => theme.color.success};
  `
}

const SIZE_CONTAINER_STYLES_MAP: Record<Size, any> = {
  default: css`
    padding-left: 24px;
    padding-right: 24px;
    font-size: 18px;
    border-radius: 55px;
    min-height: 60px;
  `,
  small: css`
    padding-left: 16px;
    padding-right: 16px;
    font-size: 14px;
    border-radius: 24px;
    height: 34px;
  `
}

type ContainerProps = {
  state: States
  gap: number
  marginBottom: number
  fullWidth: boolean
  withBackground: boolean
  size: Size
}

const Container = styled(Flex)<ContainerProps>`
  position: relative;
  align-items: center;
  margin-bottom: ${({ marginBottom }) => marginBottom}px;
  border: 1px solid ${({ theme }) => theme.color.base.c3};

  ${({ fullWidth }) => fullWidth && `width: 100%;`}
  ${({ width }) => width && `width: ${width}px;`}
  ${({ state }) => STATES_CONTAINER_STYLES_MAP[state]}
  ${({ size }) => SIZE_CONTAINER_STYLES_MAP[size]}
  ${({ withBackground, theme }) =>
    withBackground && `background-color: ${theme.color.base.c0};`}
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 1;
  margin-right: 8px;
`

type InputWrapperProps = {
  hasContent: boolean
  size: Size
}

const SIZE_INPUTWRAPPER_STYLES_MAP: Record<Size, any> = {
  default: css<InputWrapperProps>`
    padding: ${({ hasContent }) =>
      hasContent ? '28px 0px 12px' : '20px 0px 20px'};

    &:focus-within {
      padding: 28px 0px 12px;
    }
  `,
  small: css<InputWrapperProps>`
    padding: 0px 0px 0px;
    height: 100%;

    &:focus-within {
      padding: 0px 0px 0px;
    }
  `
}

const InputWrapper = styled.div<InputWrapperProps>`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;

  ${({ size }) => SIZE_INPUTWRAPPER_STYLES_MAP[size]}
`

type StyledInputProps = {
  fontSize?: number
  textOverflow: 'clip' | 'ellipsis'
}
const StyledInput = styled.input<StyledInputProps>`
  border: none;
  width: 100%;
  background: none;
  padding: 0;
  text-overflow: ${({ textOverflow }) => textOverflow};
  ${({ fontSize }) => fontSize && `font-size: ${fontSize}px;`}

  &::placeholder {
    line-height: 1;
  }
`

type LabelProps = {
  state: States
  hasContent: boolean
  size: Size
}

const STATES_LABEL_STYLES_MAP: StylesMap<States> = {
  [States.default]: css<ContainerProps>`
    color: ${({ theme }) => theme.color.base.c8};
    ${Container}:focus-within & {
      color: ${({ theme }) => theme.color.brand_02};
    }
  `,
  [States.disabled]: css``,
  [States.error]: css<ContainerProps>`
    color: ${({ theme }) => theme.color.error} !important;
  `,
  [States.success]: css`
    color: ${({ theme }) => theme.color.success} !important;
  `
}

const SIZE_LABEL_STYLES_MAP: Record<Size, any> = {
  default: css<LabelProps>`
    top: ${({ hasContent }) => (hasContent ? '10px' : '18px')};
    font-size: ${({ hasContent }) => (hasContent ? '14px' : '18px')};

    ${Container}:focus-within & {
      top: 10px;
      font-size: 14px;
    }
  `,
  small: css<LabelProps>`
    top: 50%;
    transform: translateY(-50%);
    font-size: 14px;
    opacity: ${({ hasContent }) => (hasContent ? '0' : '1')};
    visibility: ${({ hasContent }) =>
      hasContent ? 'hidden' : 'visible'};

    ${Container}:focus-within & {
      top: 50%;
      transform: translateY(-50%);
      font-size: 14px;
      opacity: 0;
      visibility: hidden;
    }
  `
}

const Label = styled.label<LabelProps>`
  position: absolute;
  left: 0;
  transition: all 0.3s;
  color: ${({ theme }) => theme.color.base.c5};
  z-index: 1;
  background: ${({ theme }) => theme.color.base.c0};
  padding: 0 4px;

  ${({ state }) => STATES_LABEL_STYLES_MAP[state]}
  ${({ size }) => SIZE_LABEL_STYLES_MAP[size]}
`

type InfoLabelProps = {
  state: States
}

const STATES_INFOLABEL_STYLES_MAP: StylesMap<States> = {
  [States.default]: css<ContainerProps>``,
  [States.disabled]: css``,
  [States.error]: css<ContainerProps>`
    color: ${({ theme }) => theme.color.error};
  `,
  [States.success]: css`
    color: ${({ theme }) => theme.color.success};
  `
}

const InfoLabel = styled.span<InfoLabelProps>`
  font-size: 12px;
  font-weight: 400;
  margin-left: 24px;
  color: ${({ theme }) => theme.color.base.c8};
  ${({ state }) => STATES_INFOLABEL_STYLES_MAP[state]}
`

export type InputProps = {
  value: string
  label?: string
  readOnly?: boolean
  onChange?: (newValue: string) => void
  renderIcon?: () => JSX.Element
  placeholder?: string
  fontSize?: number
  gap?: number
  marginBottom?: number
  inputTextOverflow?: StyledInputProps['textOverflow']
  fullWidth?: boolean
  onBlur?: () => void
  append?: JSX.Element
  isInvalid?: boolean
  isSuccess?: boolean
  id?: string
  type?: string
  infoLabel?: string
  width?: number
  padding?: [number, number]
  isDisabled?: boolean
  withBackground?: boolean
  size?: Size
}

const Input = ({
  value,
  placeholder,
  onChange = () => null,
  fontSize = 14,
  gap = 14,
  renderIcon,
  marginBottom = 0,
  readOnly = false,
  label,
  inputTextOverflow = 'clip',
  fullWidth = false,
  onBlur,
  append,
  isInvalid = false,
  isSuccess = false,
  type = 'text',
  isDisabled,
  id,
  infoLabel,
  withBackground = false,
  size = 'default',
  ...rest
}: InputProps) => {
  const getInnerState = () => {
    if (isDisabled) return States.disabled
    if (isInvalid) return States.error
    if (isSuccess) return States.success
    return States.default
  }

  const innerState = getInnerState()

  return (
    <>
      <Container
        gap={gap}
        marginBottom={marginBottom}
        fullWidth={fullWidth}
        state={innerState}
        withBackground={withBackground}
        size={size}
        {...rest}
      >
        {renderIcon && <IconWrapper>{renderIcon()}</IconWrapper>}

        <InputWrapper hasContent={Boolean(value)} size={size}>
          {label && (
            <Label
              state={innerState}
              hasContent={Boolean(value)}
              size={size}
              htmlFor={id || label || 'input'}
            >
              {label}
            </Label>
          )}

          <StyledInput
            id={id || label || 'input'}
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            fontSize={fontSize}
            readOnly={readOnly}
            textOverflow={inputTextOverflow}
            onBlur={onBlur}
            disabled={isDisabled}
            placeholder={placeholder}
          />
        </InputWrapper>

        {append && append}
      </Container>
      {infoLabel && (
        <InfoLabel state={innerState}>{infoLabel}</InfoLabel>
      )}
    </>
  )
}

export default Input
